import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import { UserInfo } from '../data/models/user.model';
import { PhoneLookupFields } from '../utils/phonePill';

export interface UserState {
  value: UserInfo;
  // A phone verification re-run's result, kept beside the user rather than in
  // it: replacing `value` resets the record form and loses unsaved edits.
  phoneLookup: { userId: string; fields: PhoneLookupFields } | null;
  status: 'idle' | 'loading' | 'failed';
}

const initialState: UserState = {
  value: {
    userId: '',
    companyId: '',
    email: '',
    personalEmail: '',
    isAdmin: false,
    state: '',
    city: '',
    photoUrl: '',
    firstName: '',
    lastName: '',
    hasAcceptedTerms: false,
    hasBenefitsPage: false,
    permissions: {
      admin: false,
      care: false,
      estate: false,
      grief: false,
      events: false,
    },
    companyName: '',
    lastLoginDate: '',
    createdDate: '',
    totalLogins: 0,
    isEmployee: false,
    isActive: false,
    activationLink: '',
    externalId: '',
  },
  phoneLookup: null,
  status: 'idle',
};

export const userSlide = createSlice({
  name: 'user',
  initialState,
  reducers: {
    saveUser: (state, action: PayloadAction<UserInfo>) => {
      state.value = action.payload;
      state.phoneLookup = null;
    },
    savePhoneLookup: (
      state,
      action: PayloadAction<{ userId: string; fields: PhoneLookupFields }>,
    ) => {
      // A re-run that resolves after the admin has moved to another member.
      if (action.payload.userId !== state.value.userId) return;
      state.phoneLookup = action.payload;
    },
    clearUser: (state) => {
      state.phoneLookup = null;
      state.value = {
        userId: '',
        companyId: '',
        email: '',
        personalEmail: '',
        isAdmin: false,
        state: '',
        city: '',
        photoUrl: '',
        firstName: '',
        lastName: '',
        hasAcceptedTerms: false,
        hasBenefitsPage: false,
        permissions: {
          admin: false,
          care: false,
          estate: false,
          grief: false,
          events: false,
        },
        companyName: '',
        lastLoginDate: '',
        createdDate: '',
        totalLogins: 0,
        isEmployee: false,
        isActive: false,
        activationLink: '',
        externalId: '',
      };
    },
  },
});

export const { saveUser, savePhoneLookup, clearUser } = userSlide.actions;
export const selectUser = (state: RootState) => state.user.value;
export const selectPhoneLookup = (state: RootState) => state.user.phoneLookup;
export default userSlide.reducer;
